import React, { useState, useEffect } from 'react';
import { ArrowPathIcon, EyeIcon } from '@heroicons/react/24/outline';
import { api } from '../services/api';
import toast from '../utils/toast';

const ViewStatusPage = () => {
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState('');
  const [agentStatus, setAgentStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(false);

  useEffect(() => {
    fetchAgents();
  }, []);

  const fetchAgents = async () => {
    try {
      setLoading(true);
      const response = await api.getClients();
      setAgents(response.data.clients || []);
    } catch (error) {
      console.error('Error fetching agents:', error);
      toast.error('Failed to load WhatsApp agents');
    } finally {
      setLoading(false);
    }
  };

  const handleViewStatus = async () => {
    if (!selectedAgent) {
      toast.error('Please select an agent');
      return;
    }

    setFetching(true);
    try {
      const agent = agents.find(a => a.id === selectedAgent);
      if (!agent) {
        toast.error('Agent not found');
        return;
      }

      // This would need the actual API endpoint for getting status
      // For now, mock some status data
      setAgentStatus({
        status: 'available',
        last_seen: new Date().toISOString(),
        profile_name: agent.name,
        phone_number: agent.phone_number,
        connection_status: agent.status
      });
      
      toast.info('Status viewing functionality coming soon');
    } catch (error) {
      console.error('Error fetching status:', error);
      toast.error('Failed to fetch status');
    } finally {
      setFetching(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <ArrowPathIcon className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">View WhatsApp Status</h1>
          <p className="text-gray-600 mt-1">Check the current status of your WhatsApp agents</p>
        </div>
        <button
          onClick={fetchAgents}
          className="btn-secondary btn-sm"
        >
          <ArrowPathIcon className="h-4 w-4 mr-2" />
          Refresh
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select WhatsApp Agent
            </label>
            <select
              value={selectedAgent}
              onChange={(e) => setSelectedAgent(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            >
              <option value="">Choose an agent...</option>
              {(Array.isArray(agents) ? agents : []).map((agent) => (
                <option key={agent.id} value={agent.id}>
                  {agent.name} ({agent.phone_number}) - {agent.status}
                </option>
              ))}
            </select>
          </div>

          <button
            onClick={handleViewStatus}
            disabled={!selectedAgent || fetching}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-3 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {fetching ? (
              <>
                <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
                Fetching...
              </>
            ) : (
              <>
                <EyeIcon className="h-4 w-4 mr-2" />
                View Status
              </>
            )}
          </button>

          {agentStatus && (
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Agent Status</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <span className="text-sm font-medium text-gray-500">Profile Name:</span>
                  <p className="text-sm text-gray-900">{agentStatus.profile_name}</p>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-500">Phone Number:</span>
                  <p className="text-sm text-gray-900">{agentStatus.phone_number}</p>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-500">Connection Status:</span>
                  <p className="text-sm text-gray-900 capitalize">{agentStatus.connection_status}</p>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-500">Last Seen:</span>
                  <p className="text-sm text-gray-900">{new Date(agentStatus.last_seen).toLocaleString()}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ViewStatusPage;